import { Component, computed, inject, input } from "@angular/core";
import { Router } from "@angular/router";
import { NbIconModule, NbListModule, NbTooltipModule } from "@nebular/theme";
import { CourseService } from "app/@core/services/course.service";
import { ProgressService } from "app/@core/services/progress.service";
import { UserService } from "app/@core/services/users.service";
import { Course, Lesson, Module, ProgresItem, Progress } from "app/@core/types";
import { DayjsService } from "app/@core/utils/dayjs.service";
import { LineTruncationLibModule } from "../../modules/line-truncation/line-truncation.module";

@Component({
  standalone: true,
  selector: "ngx-lesson-list",
  styleUrls: ["./lesson-list.component.scss"],
  templateUrl: "./lesson-list.component.html",
  imports: [
    NbTooltipModule,
    NbListModule,
    NbIconModule,
    LineTruncationLibModule,
  ],
})
export class LessonListComponent {
  private router = inject(Router);
  private userService = inject(UserService);
  private courseService = inject(CourseService);
  private progressService = inject(ProgressService);
  private dayjs = inject(DayjsService);

  type = input<keyof Progress>();
  course = input.required<Course>();
  module = input.required<Module>();
  link = input<number>(0);
  icon = input<boolean>(true);
  nextup = input<boolean>(false);

  currentLesson: Lesson;
  user = this.userService.user;
  moduleProgress = this.progressService.moduleState;

  readonly lessons = computed(() => {
    if (this.type() === "masterclasses" && this.nextup()) {
      return this.setNextLesson();
    }
    return this.module().lessons;
  });

  readonly lessonsProgress = computed(() => {
    let isPack = this.type();
    return isPack === "masterclasses"
      ? this.progressService.masterclassState()?.[this.module().id]
      : this.progressService.moduleState()?.[this.module().id];
  });

  readonly progress = computed(() => {
    const isLoaded = this.progressService.isLoaded();
    const progress = this.progressService.progress();
    if (!isLoaded) return null;
    return progress;
  });

  setNextLesson() {
    const module = this.module();
    const progress = this.progress();
    let lessons: Lesson[] = [];
    const mlessons = module.lessons;

    let pmlessons: ProgresItem[] = [];
    mlessons.forEach((keys: any) => {
      if (progress?.[this.type()].hasOwnProperty(keys.id)) {
        pmlessons.push(progress?.[this.type()]?.[keys.id]);
      }
    });

    let nextup = pmlessons
      .filter((lesson: ProgresItem) => lesson.status == "Started")
      .sort((a, b) => this.dayjs.compareDesc(b.start_date, a.start_date))
      .slice(0, 1)[0];
    if (nextup) {
      lessons = module.lessons.filter(
        (lesson: Lesson) => lesson.id == nextup.id,
      );
    } else {
      lessons = module.lessons.slice(0, 1);
    }
    return lessons;
  }

  goToLesson(lesson: Lesson): void {
    if (this.type() == "masterclasses") {
      this.router.navigate(
        [
          "course",
          this.course().slug,
          this.type(),
          this.module().slug,
          lesson.slug,
        ],
        {
          state: {
            course: this.course().name,
            module: this.module().name,
            lesson: lesson.name,
          },
        },
      );
    } else {
      this.courseService.loadLesson(lesson.slug);
      this.router.navigate([
        this.course().slug == "aces" ? "courses" : "course",
        this.course().slug,
        this.module().slug,
        lesson.slug,
      ]);
    }
  }
}
